import { EditorState, Modifier, RichUtils } from "draft-js";
import "draft-js/dist/Draft.css";
import { useState } from "react";
import { v4 as uuid } from "uuid";

import { Button, makeStyles } from "@material-ui/core";

import DisabledButtonWithTooltip from "src/components/DisabledButtonWithTooltip";
import FormDraftEditor from "src/components/form/FormDraftEditor";
import { CampaignWithCalleeFields_calleeFields } from "src/graphql/generated/CampaignWithCalleeFields";
import { getCampaign_campaign_calleeFields } from "src/graphql/generated/getCampaign";
import palette from "src/styles/palette";

import AddCalleeFieldForm from "./AddCalleeFieldForm";
import { AllowedCommand, INLINE } from "./Toolbar";
import { makeCopyPasteHandlers } from "./copyPaste";
import { entityDataForCalleeField } from "./serialization";

const useStyles = makeStyles(theme => ({
  container: {
    marginBottom: "30px"
  },
  calleeFieldsWrapper: {
    marginTop: "10px"
  },
  calleeFieldsExisting: {
    marginBottom: "5px"
  },
  calleeFieldButton: {
    padding: "0 0.5rem",
    minWidth: "0",
    marginRight: "5px",
    marginBottom: "5px",
    border: "2px solid",
    borderColor: palette.navy,
    borderRadius: "1rem",
    background: theme.palette.primary.main,
    color: "white",
    textTransform: "none",
    "&:hover": {
      background: palette.darkBlue,
      borderColor: palette.darkBlue
    },
    "&:disabled": {
      background: palette.lightGray,
      borderColor: palette.lightGray
    }
  }
}));

export default function TextEditor({
  label,
  placeholder,
  calleeFields,
  canUpdateCalleeFields,
  editorState,
  enableRichText = true,
  onChangeEditorState,
  onCreateCalleeField,
  error
}: {
  label: string;
  placeholder: string;
  calleeFields: getCampaign_campaign_calleeFields[];
  canUpdateCalleeFields: boolean;
  editorState: EditorState;
  enableRichText?: boolean;
  onChangeEditorState: (newState: EditorState) => any;
  onCreateCalleeField: (
    name: string
  ) => Promise<CampaignWithCalleeFields_calleeFields>;
  error?: boolean;
}): JSX.Element {
  const classes = useStyles();

  const [newCalleeFieldFormOpen, setNewCalleeFieldFormOpen] = useState(false);

  const insertCalleeField = (field: CampaignWithCalleeFields_calleeFields) => {
    const selection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "CALLEE_FIELD",
      "IMMUTABLE",
      entityDataForCalleeField(field)
    );
    const contentStateWithCalleeField = Modifier.replaceText(
      contentStateWithEntity,
      selection,
      field.name,
      undefined,
      contentStateWithEntity.getLastCreatedEntityKey()
    );
    const editorStateWithCalleeField = EditorState.push(
      editorState,
      contentStateWithCalleeField,
      "insert-fragment"
    );
    const editorStateWithSelection = EditorState.forceSelection(
      editorStateWithCalleeField,
      contentStateWithCalleeField.getSelectionAfter()
    );
    onChangeEditorState(editorStateWithSelection);
  };

  const insertNewCalleeField = async (name: string) => {
    const newField = await onCreateCalleeField(name);
    insertCalleeField(newField);
    setNewCalleeFieldFormOpen(false);
  };

  const { onCopy, onCut, handlePastedText } = makeCopyPasteHandlers(
    onChangeEditorState,
    calleeFields,
    enableRichText
  );

  const handleKeyCommand = (command: string, type: AllowedCommand) => {
    const editorStateFocused = EditorState.forceSelection(
      editorState,
      editorState.getSelection()
    );
    const newState =
      type === INLINE
        ? RichUtils.toggleInlineStyle(editorStateFocused, command)
        : RichUtils.toggleBlockType(editorStateFocused, command);

    if (newState) {
      onChangeEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  return (
    <div className={classes.container}>
      <FormDraftEditor
        label={label}
        editorState={editorState}
        onChange={onChangeEditorState}
        onStyleToggle={handleKeyCommand}
        placeholder={placeholder}
        onCopy={onCopy}
        onCut={onCut}
        handlePastedText={handlePastedText}
        enableRichText={enableRichText}
        error={error}
      />
      <div className={classes.calleeFieldsWrapper}>
        <div className={classes.calleeFieldsExisting}>
          {calleeFields.map(calleeField =>
            canUpdateCalleeFields || calleeField.dataSourceFieldName ? (
              <Button
                className={classes.calleeFieldButton}
                onClick={() => insertCalleeField(calleeField)}
                key={calleeField.name || calleeField.builtInField || uuid()}
              >
                + {calleeField.name}
              </Button>
            ) : (
              <DisabledButtonWithTooltip
                className={classes.calleeFieldButton}
                key={calleeField.name || calleeField.builtInField || uuid()}
                tooltipTitle="Map this field to a CSV column before using"
              >
                + {calleeField.name}
              </DisabledButtonWithTooltip>
            )
          )}
        </div>
        <div>
          {newCalleeFieldFormOpen ? (
            <AddCalleeFieldForm
              onSubmit={insertNewCalleeField}
              onCancel={() => setNewCalleeFieldFormOpen(false)}
            />
          ) : canUpdateCalleeFields ? (
            <Button
              onClick={() => setNewCalleeFieldFormOpen(true)}
              variant="contained"
              disableElevation
            >
              + New Contact Field
            </Button>
          ) : (
            <DisabledButtonWithTooltip
              variant="contained"
              disableElevation
              tooltipTitle="Add callee fields from the Upload Contacts tab"
            >
              + New Contact Field
            </DisabledButtonWithTooltip>
          )}
        </div>
      </div>
    </div>
  );
}
